import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Section, Input, Textarea, Button, Selectable, Cell } from '@telegram-apps/telegram-ui'

import { api } from '../../lib/api'
import type { Me } from '../../lib/types'
import BackHeader from '../../components/BackHeader'
import { todayISO, addDaysISO } from '../../lib/format'
import { hapticSuccess, hapticError } from '../../lib/telegram'

type Assignee = 'me' | 'secretary' | 'driver'
type Category = 'work' | 'personal'

export default function DirectorCreateTask() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = qc.getQueryData<Me>(['me'])
  const secName = me?.secretary_name || 'Анастасия'
  const drvName = me?.driver_name || 'Владимир'

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assignee, setAssignee] = useState<Assignee>('secretary')
  const [category, setCategory] = useState<Category>('work')
  const [deadline, setDeadline] = useState(addDaysISO(1))

  const create = useMutation({
    mutationFn: () => api.post('/tasks', {
      title,
      description,
      assignee,
      category,
      deadline,
    }),
    onSuccess: () => {
      hapticSuccess()
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      navigate(-1)
    },
    onError: () => hapticError(),
  })

  return (
    <>
      <BackHeader title="Новая задача" />

      <Section>
        <Input
          header="Название"
          placeholder="Например, забронировать билеты"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea
          header="Описание (опционально)"
          placeholder="Детали, важные нюансы"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Section>

      <Section header="Исполнитель">
        <Cell
          Component="label"
          before={<Selectable name="assignee" value="me" checked={assignee === 'me'} onChange={() => setAssignee('me')} />}
        >
          Я сам
        </Cell>
        <Cell
          Component="label"
          before={<Selectable name="assignee" value="secretary" checked={assignee === 'secretary'} onChange={() => setAssignee('secretary')} />}
        >
          {secName}
        </Cell>
        <Cell
          Component="label"
          before={<Selectable name="assignee" value="driver" checked={assignee === 'driver'} onChange={() => setAssignee('driver')} />}
        >
          {drvName}
        </Cell>
      </Section>

      <Section header="Тип">
        <Cell
          Component="label"
          before={<Selectable name="category" value="work" checked={category === 'work'} onChange={() => setCategory('work')} />}
        >
          💼 Рабочая
        </Cell>
        <Cell
          Component="label"
          before={<Selectable name="category" value="personal" checked={category === 'personal'} onChange={() => setCategory('personal')} />}
        >
          🏠 Личная
        </Cell>
      </Section>

      <Section header="Дедлайн">
        <div style={{ padding: '8px 16px' }}>
          <input
            type="date"
            value={deadline}
            min={todayISO()}
            onChange={(e) => setDeadline(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: 16,
              borderRadius: 12,
              border: '1px solid var(--tgui--divider, #ddd)',
              background: 'var(--tgui--bg_color, #fff)',
              color: 'var(--tgui--text_color, #000)',
            }}
          />
        </div>
      </Section>

      <div style={{ padding: 16 }}>
        <Button
          stretched
          size="l"
          disabled={!title.trim() || create.isPending}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Создать
        </Button>
      </div>
    </>
  )
}
